import { useState, Suspense } from 'react';
import { useAppContext, SET_DESIGN_NAME } from '../context/AppContext';
import { useNavigate } from 'react-router-dom';
import { useUrlState } from '../utils/urlState';
import PreviewSVG from '../components/PreviewSVG';
import { componentMap } from '../components/ChoosePlantSideWindow';
import { Box, Text, Group, Button, TextInput, Stack, Paper } from '@mantine/core';
import { colors, fontSize } from '../constants';

const SummaryOneMobile = () => {
	const [userInput, setUserInput] = useState('');
	const [isEditing, setIsEditing] = useState(false);
	const [error, setError] = useState('');
	const navigate = useNavigate();
	const { navigateWithParams } = useUrlState();
	const { state, dispatch } = useAppContext();

	// Helper function to humanize values
	const humanizeValue = (value) => {
		if (!value) return value;
		return value
			.split('_')
			.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
			.join(' ');
	};
	const {
		designName,
		zone,
		style,
		sunlight,
		windows,
		facade,
		houseSize,
		shape,
		bedSize,
		bedConfig,
		chosenPlants,
	} = state.currentDesign;

	const SelectedGardenBed = bedConfig ? componentMap[bedConfig] : null;

	// Handle design name submission
	const handleChangeDesignName = (event) => {
		setUserInput(event.target.value);
	};

	// Handle submitting the design name
	const handleSubmitDesignName = () => {
		if (userInput.trim()) {
			dispatch({ type: SET_DESIGN_NAME, payload: userInput });
			setIsEditing(false);
			setError('');
		}
	};

	// Handle editing the design name
	const handleEditDesignName = () => {
		setIsEditing(true);
		setUserInput(designName || '');
	};

	// Navigate to the next screen
	const handleNext = () => {
		if (!designName) {
			setError('Please set a design name before proceeding.');
			return;
		}
		navigateWithParams('/summary-two');
	};

	// Navigate to the previous screen
	const handleBack = () => {
		navigate('/choose-plants');
	};

	console.log('Zone: ', zone);
	console.log('Style: ', style);
	console.log('Sunlight: ', sunlight);
	console.log('Windows: ', windows);
	console.log('Facade: ', facade);
	console.log('House Size: ', houseSize);
	console.log('Shape: ', shape);
	console.log('Bed Size: ', bedSize);
	console.log('BedId: ', bedConfig);
	console.log('ChosenPlants: ', chosenPlants);

	return (
		<Box style={{ 
			height: '100vh', 
			width: '100vw',
			display: 'flex', 
			flexDirection: 'column',
			backgroundColor: colors.mobileBackground,
			position: 'fixed',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			overflow: 'hidden',
			boxSizing: 'border-box'
		}}>
			{/* Header */}
			<Box style={{
				backgroundColor: colors.backgroundLight,
				height: '120px',
				padding: '10px 10px',
				zIndex: 10,
				position: 'relative',
				borderBottom: `12px solid ${colors.white}`,
				display: 'flex',
				flexDirection: 'column',
				justifyContent: 'flex-end',
				alignItems: 'center'
			}}>
				<Text style={{
					color: colors.white,
					fontSize: fontSize.xxxl,
					fontWeight: 'bold',
					textAlign: 'center'
				}}>
					Summary
				</Text>
			</Box>

			{/* Navigation Bar */}
			<Group justify="space-between" align="center" style={{ 
				padding: '2.5vh 5vw',
				zIndex: 10,
				position: 'relative',
				height: '10.5vh',
				flexShrink: 0,
			}}>
				<Button
					variant="filled"
					onClick={handleBack}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '8px',
							padding: '8px 20px',
							fontWeight: 'bold',
							minHeight: '44px',
							fontSize: fontSize.lg,
						}
					}}
				>
					Back
				</Button>
				
				<Text style={{
					color: colors.white,
					fontSize: fontSize.xl,
					fontWeight: 'bold',
					textAlign: 'center'
				}}>
					Plot Review
				</Text>
				
				<Button
					variant="filled"
					onClick={handleNext}
					styles={{
						root: {
							backgroundColor: colors.buttonColor,
							color: colors.white,
							border: 'none',
							borderRadius: '8px',
							padding: '8px 20px',
							fontWeight: 'bold',
							minHeight: '44px',
							fontSize: fontSize.lg,
						}
					}}
				>
					Next
				</Button>
			</Group>

			{/* Main Content Area - PreviewSVG Background */}
			<Box style={{ 
				flex: 1,
				minHeight: 0,
				position: 'relative',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
				paddingBottom: '25vh' // Space for the fixed bottom panel
			}}>
				{/* PreviewSVG fills the space as background */}
				<Box style={{
					flex: 1,
					minHeight: 0,
					position: 'relative',
					display: 'flex',
					alignItems: 'flex-start',
					justifyContent: 'center',
					overflow: 'hidden',
				}}>
					<PreviewSVG styleOverride={{ width: '100vw' }} />
				</Box>
			</Box>

			{/* Bottom Card - Fixed at bottom */}
			<Paper 
				radius="md"
				style={{
					position: 'fixed',
					bottom: '4vh',
					left: 0,
					right: 0,
					zIndex: 1000,
					backgroundColor: colors.darkerGrey,
          paddingTop: '2vh',
					paddingLeft: '2vw',
          paddingRight: '1vw',
          paddingBottom: '2vh',
          height: '35vh',
					maxHeight: '40vh',
					overflow: 'hidden',
					margin: '0 5vw',
					width: '90vw',
				}}>
					{/* Design Name Section */}
					<Box style={{ marginBottom: '4vh' }}>
						<Group justify="center" align="center" style={{ marginBottom: '1vh' }}>
							<Text style={{
								fontSize: fontSize.xxl,
								fontWeight: 'bold',
								color: colors.white
							}}>
								{designName}
							</Text>
							{designName && !isEditing && (
								<Button
									variant="subtle"
									onClick={handleEditDesignName}
									styles={{
										root: {
											padding: '4px 0px',
											minHeight: 'auto',
											fontSize: fontSize.lg,
											color: colors.white,
											backgroundColor: 'transparent'
										}
									}}
								>
								<svg
									width='24'
									height='24'
									viewBox='0 0 72 72'
									fill='none'
									xmlns='http://www.w3.org/2000/svg'
								>
										<path
											d='M0 57.01V72.01H15L59.24 27.77L44.24 12.77L0 57.01ZM70.84 16.17C72.4 14.61 72.4 12.09 70.84 10.53L61.48 1.17C59.92 -0.39 57.4 -0.39 55.84 1.17L48.52 8.49L63.52 23.49L70.84 16.17Z'
											fill='white'
										/>
									</svg>
								</Button>
							)}
						</Group>
						
						{(!designName || isEditing) && (
							<Group gap="2vw" align="center">
								<TextInput
									placeholder="Please name your design"
									value={userInput}
									onChange={handleChangeDesignName}
									styles={{
										input: {
											borderRadius: '8px',
											border: `1px solid ${colors.border}`,
											fontSize: fontSize.base,
											padding: '12px',
											backgroundColor: colors.white,
											height: '44px'
										}
									}}
									style={{ flex: 1 }}
								/>
								<Button
									onClick={handleSubmitDesignName}
									styles={{
										root: {
											backgroundColor: colors.buttonColor,
											color: colors.white,
											border: 'none',
											borderRadius: '8px',
											padding: '8px 16px',
											fontWeight: 'bold',
											fontSize: fontSize.base,
											whiteSpace: 'nowrap',
											height: '44px',
											minHeight: '44px'
										}
									}}
								>
									Submit
								</Button>
							</Group>
						)}
						
						{error && (
							<Text style={{
								color: colors.error,
								fontSize: fontSize.sm,
								marginTop: '10px'
							}}>
								{error}
							</Text>
						)}
					</Box>

					{/* Garden Bed Layout and Design Details */}
					<Group align="flex-start" gap="2vw" style={{ marginTop: '15px' }}>
						{/* Garden Bed Layout */}

            <Box style={{
              width: '60%',
              height: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: colors.white,
              borderRadius: '8px',
              padding: '8px',
              flex: 1.9
            }}>
              {SelectedGardenBed ? (
                <Suspense fallback={
                  <Text style={{ color: colors.text, fontSize: fontSize.xs }}>
                    Loading...
                  </Text>
                }>
                  <SelectedGardenBed />
                </Suspense>
              ) : (
                <Text style={{ color: colors.text, fontSize: fontSize.xs }}>
                  No garden bed found
                </Text>
              )}
            </Box>

						{/* Design Details */}
						<Box style={{
							flex: 1.1,
							minWidth: 0,
							width: '40%'
						}}>
							
							<Stack gap="0px">
								<Text style={{ fontSize: fontSize.base, color: colors.white, textAlign: 'left' }}>
									<strong>Zone:</strong> {zone}
								</Text>
								<Text style={{ fontSize: fontSize.base, color: colors.white, textAlign: 'left' }}>
									<strong>Sunlight:</strong> {humanizeValue(sunlight)}
								</Text>
								<Text style={{ fontSize: fontSize.base, color: colors.white, textAlign: 'left' }}>
									<strong>Style:</strong> {humanizeValue(style)}
								</Text>
								<Text style={{ fontSize: fontSize.base, color: colors.white, textAlign: 'left' }}>
									<strong>Bed Size:</strong> {bedSize}
								</Text>
							</Stack>
						</Box>
					</Group>
				</Paper>
		</Box>
	);
};

export default SummaryOneMobile;
